import React, { useEffect, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import "./App.css";
import "react-toastify/dist/ReactToastify.css";
import "pure-react-carousel/dist/react-carousel.es.css";

import Login from "./views/Auth/Login";
import Register from "./views/Auth/Register";
import OurTeam from "./views/OurTeam/index";
import Leaders from "./views/Leaders";
import UserProfile from "./views/Users";
import ChangePassword from "./views/Users/ChangePassword";
import Meetings from "./views/Users/Meetings";
import ForgotPassword from "./views/Auth/ForgotPassword";
import PaymentHistory from "./views/Users/PaymentHistory";
import RequestMeeting from "./views/Leaders/LeaderProfile";
import Payment from "./views/Users/Payment";
import Confirmation from "./views/Leaders/Confirmation";
import Accept from "./views/Meetings/Accept";
import RejectWithNote from "./views/Meetings/Reject_with_note";
import CancelWithNote from "./views/Meetings/Cancel_with_note";
import Review from "./views/Meetings/Review";
import Concerns from "./views/Meetings/Concerns";
import Wishes from "./views/Wishes";
import TermsConditions from "./views/Terms&Conditions/Terms&Conditions";
import PrivacyPolicy from "./views/PrivacyPolicy/PrivacyPolicy";
import ImpersonationBanner from "./components/General/ImpersonationBanner";

const BecomeLeader = lazy(() => import("./views/BecomeLeader"));
const BecomeLearner = lazy(() => import("./views/BecomeLearner"));
const Dashboard = lazy(() => import("./views/Dashboard"));
const Introduction = lazy(() => import("./views/Introduction"));
const ConversationQuestions = lazy(() => import("./views/ConversationQuestions"));
const MyanmarEarthquake = lazy(() => import("./views/MyanmarEarthquake"));
const Java = lazy(() => import("./views/JavaSyllabus"));

// FUHSD-specific components
const FuhsdDashboard = lazy(() => import("./views/Dashboard/FuhsdDashboard"));
const FuhsdIntroduction = lazy(() => import("./views/Introduction/FuhsdIntroduction"));
const FuhsdBecomeLeader = lazy(() => import("./views/BecomeLeader/FuhsdBecomeLeader"));
const FuhsdBecomeLearner = lazy(() => import("./views/BecomeLearner/FuhsdBecomeLearner"));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  // Detect subdomain
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  
  // Handle both localhost and production domains
  let subdomain = null;
  if (parts.length === 2 && parts[1] === "localhost") {
    // For fuhsd.localhost
    subdomain = parts[0] !== "localhost" ? parts[0] : null;
  } else if (parts.length > 2) {
    // For fuhsd.lingoclub.org
    subdomain = parts[0];
  }
  
  const isFuhsd = subdomain === "fuhsd";

  // Debug logging
  console.log("Hostname:", hostname, "| Subdomain:", subdomain, "| isFuhsd:", isFuhsd);

  return (
    <Router>
      <ImpersonationBanner />
      <ScrollToTop />

      <Routes>
        <Route
          path="/"
          element={
            <React.Suspense fallback={<>...loading...</>}>
              {isFuhsd ? <FuhsdDashboard /> : <Dashboard />}
            </React.Suspense>
          }
        />
        <Route
          path="/introduction"
          element={
            <React.Suspense fallback={<>...loading...</>}>
              {isFuhsd ? <FuhsdIntroduction /> : <Introduction />}
            </React.Suspense>
          }
        />
        <Route
          path="/conversation-questions"
          element={
            <React.Suspense fallback={<>...loading...</>}>
              <ConversationQuestions />
            </React.Suspense>
          }
        />
        <Route
          path="/Myanmar-earthquake-relief-efforts"
          element={
            <React.Suspense fallback={<>...loading...</>}>
              <MyanmarEarthquake />
            </React.Suspense>
          }
        />
        <Route
          path="/Java-Syllabus"
          element={
            <React.Suspense fallback={<>...loading...</>}>
              <Java />
            </React.Suspense>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/our-team" element={<OurTeam />} />
        <Route path="/courses/accept/:id" element={<Accept />} />
        <Route path="/courses/reject_with_note/:id" element={<RejectWithNote />} />
        <Route path="/courses/cancel_with_note/:id" element={<CancelWithNote />} />
        <Route path="/courses/review/:id" element={<Review />} />
        <Route path="/courses/concerns/:id" element={<Concerns />} />
        <Route
          path="/become-a-leader"
          element={
            <React.Suspense fallback={<>...</>}>
              {isFuhsd ? <FuhsdBecomeLeader /> : <BecomeLeader />}
            </React.Suspense>
          }
        />
        <Route
          path="/become-a-learner"
          element={
            <React.Suspense fallback={<>...</>}>
              {isFuhsd ? <FuhsdBecomeLearner /> : <BecomeLearner />}
            </React.Suspense>
          }
        />
        <Route path="/schedule-meeting" element={<Leaders />} />
        <Route path="/leaders/profile/:id" element={<RequestMeeting />} />
        <Route path="/leaders/confirmation/:id/" element={<Confirmation />} />
        <Route path="/user/profile" element={<UserProfile inputTab="profile"/>} />
        <Route path="/user/leader-profile" element={<UserProfile inputTab="leaderProfile"/>} />
        <Route path="/user/changePassword" element={<ChangePassword />} />
        <Route path="/user/meetings" element={<Meetings />} />
        <Route path="/user/payment" element={<Payment />} />
        <Route path="/user/paymentHistory" element={<PaymentHistory />} />
        <Route path="/make-a-wish" element={<Wishes />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
    </Router>
  );
}

export default App;
